// apps/web/src/components/consent-checkbox.tsx — Phase 4 plan 04-03.
//
// DSGVO consent block for the A5 wizard. The consent text is
// operator-overridable via `Restaurant.consentText[locale]` and
// falls back to the web-bundled `CONSENT_DEFAULTS` (see
// `apps/web/src/lib/consent-defaults.ts`) when the API returns
// null in both DE and EN (D-15 / DSG-05).
//
// The `consentAt` timestamp is captured at the checkbox-click
// moment — NOT at the A6 submit click (D-15 / DSG-05). The
// parent passes `onCheck(checked, consentAt)` and persists the
// timestamp on the wizard state; A6 reads it verbatim when it
// builds the createReservation input.

import { useTranslation } from "react-i18next";

export interface ConsentCheckboxErrors {
  consentText?: string | undefined;
  accepted?: string | undefined;
}

export interface ConsentCheckboxProps {
  consentText: string;
  checked: boolean;
  onCheck: (checked: boolean, consentAt: string) => void;
  errors?: ConsentCheckboxErrors | undefined;
}

export function ConsentCheckbox({
  consentText,
  checked,
  onCheck,
  errors,
}: ConsentCheckboxProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <div
      className="space-y-2 border border-divider rounded-md p-3"
      data-testid="consent-checkbox"
    >
      <div
        className="max-h-32 overflow-y-auto border border-divider rounded p-2 text-sm text-text/90 bg-surface"
        data-testid="consent-text"
      >
        {consentText}
      </div>
      <label className="flex items-start gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onCheck(e.target.checked, new Date().toISOString())}
          data-testid="consent-input"
          className="mt-1 h-4 w-4 accent-accent"
        />
        <span className="text-sm text-text">{t("wizard.a5.consentLabel")}</span>
      </label>
      {errors?.consentText && (
        <p
          className="text-xs text-red-700"
          data-testid="consent-text-error"
          role="alert"
        >
          {errors.consentText}
        </p>
      )}
      {errors?.accepted && (
        <p
          className="text-xs text-red-700"
          data-testid="consent-accepted-error"
          role="alert"
        >
          {errors.accepted}
        </p>
      )}
    </div>
  );
}
